import { useState, useRef, useEffect } from "react";
import { useSelector } from 'react-redux';
import PrintLink from '../PrintLink/PrintLink';
import { ChamUploadButton } from '../../features/ChamUpload/ChamUpload';
import HelpModal from '../HelpModal/HelpModal';
import ShareModal from '../ShareModal/ShareModal';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import { postSaveToParent } from '../../../Helpers/PostMessageHelper';

import styles from './TopMenu.module.css';
import closeIcon from './assets/close.svg';
import openIcon from './assets/open.svg';

export default function TopMenu({ extended }) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const menuRef = useRef(null);
    
    const currentView = useSelector(state => state.app.currentView);
    const currentImageUrl = useSelector(state => state.app.currentImageUrl);
    const style = useSelector(state => state.app.style);
    const size = useSelector(state => state.app.size);
    const activeProfile = useSelector(state => state.app.activeProfile);
    const productSelection = useSelector(state => state.app.productSelection);
    const roomscene = useSelector(state => state.app.roomscene);
    
    // Dynamic download button text based on current view
    const downloadButtonText = currentView === 'room' ? 'Download Roomview' : 'Download Swatch Image';
    const [downloadBtnStatus, setDownloadBtnStatus] = useState(downloadButtonText);

    extended = true;

    // Update download button text when view changes
    useEffect(() => {
        setDownloadBtnStatus(downloadButtonText);
    }, [downloadButtonText]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobileMenuOpen]);

    const handleMenuTriggerClick = function () {
        setIsMobileMenuOpen(status => !status);
    }

    const closeMenu = () => {
        setIsMobileMenuOpen(false);
    }

    const handleShareModalButtonClick = function () {
        setIsShareOpen(true);
        closeMenu();
    }

    const handleFeedbackModalButtonClick = function () {
        setIsFeedbackOpen(true);
        closeMenu();
    }

    async function downloadImage(imageSrc, nameOfDownload = 'my-image.jpeg') {
        try {
            const response = await fetch(imageSrc);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const blobImage = await response.blob();
            const href = URL.createObjectURL(blobImage);
            
            const anchorElement = document.createElement('a');
            anchorElement.href = href;
            anchorElement.download = nameOfDownload;
            
            document.body.appendChild(anchorElement);
            anchorElement.click();
            document.body.removeChild(anchorElement);
            
            window.URL.revokeObjectURL(href);
            console.log('The image has been downloaded');
        } catch (error) {
            console.error('Error downloading image: ', error);
            throw error;
        }
    }

    const handleDownloadButtonClick = async function () {
        const viewType = currentView === 'room' ? 'Roomview' : 'Swatch';
        setDownloadBtnStatus(`LOADING ${viewType.toUpperCase()}...`)

        try {
            // Generate high-res download URL (3000px) instead of using display URL
            let downloadUrl;
            
            if (currentView === 'room') {
                // Room view URL with size=3000
                const selectionList = productSelection.length > 0 
                    ? productSelection.join(',') 
                    : '-1';
                downloadUrl = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?spec=${roomscene}&type=base&size=3000&sel=${activeProfile.id}(${selectionList})`;
            } else {
                // Swatch view URL with size=3000
                const decalID = activeProfile?.description;
                if (!decalID) {
                    console.error('No decalID available');
                    setDownloadBtnStatus(currentView === 'room' ? 'Download Roomview' : 'Download Swatch Image');
                    return;
                }
                const selectionList = productSelection.length > 0 
                    ? productSelection.join(',') 
                    : '-1';
                downloadUrl = `https://renderfarm1.chameleonpower.com/cham/getImage.ashx?specifier=!${decalID}&sel=${selectionList}&size=3000&extra=(DESIGN=(MODEL=TILE),focus=(@size[x]/@res[x]/1in),size=(?,(@size[x]*@res[y]/@res[x])))`;
            }

            // Generate filename: "Style Size Swatch" or "Style Size Roomview"
            const fileName = `${style} ${size} ${viewType}.jpg`;
            
            await downloadImage(downloadUrl, fileName);
            setDownloadBtnStatus(currentView === 'room' ? 'Download Roomview' : 'Download Swatch Image');
            console.log(`${currentView} image downloaded successfully as: ${fileName}`);
        } catch (error) {
            console.error('Error in download function:', error);
            setDownloadBtnStatus(currentView === 'room' ? 'Download Roomview' : 'Download Swatch Image');
        }
        closeMenu();
    }

    const handleHelpButtonClick = function () {
        setIsHelpOpen(true);
        closeMenu();
    }

    const handleStartOverClick = function () {
        window.location.href = '/';
        closeMenu();
    }

    const handleSaveClick = function () {
        // Build project URL
        const projectUrl = `${window.location.origin}/style/${encodeURIComponent(style)}/size/${encodeURIComponent(size)}/products/${productSelection.join(',')}`;

        // Build the save data object
        const saveData = {
            style: style,
            size: size,
            profile: activeProfile ? activeProfile.description : null, // Use decalID from description field
            base: productSelection[0] || null,
            accent1: productSelection[1] || null,
            products: productSelection.join(','), // Combined products as comma-separated string
            room: roomscene,
            projectUrl: projectUrl
        };

        // Add accent2 if it exists
        if (productSelection.length > 2 && productSelection[2]) {
            saveData.accent2 = productSelection[2];
        }

        console.log('Saving data:', saveData);
        
        // Send post message to parent
        postSaveToParent(saveData);
        
        closeMenu();
    }

    return (
        <>
            <section className={styles.topMenu} ref={menuRef}>
                <nav aria-label="Main Navigation">
                    <ul className={!isMobileMenuOpen ? styles.closed : ''}>
                        <li className={styles.menuTrigger}>
                            <button onClick={handleMenuTriggerClick} aria-expanded={isMobileMenuOpen} aria-controls="main-menu">
                                <span aria-hidden="true">
                                    {!isMobileMenuOpen && <img src={openIcon} alt='open icon' />}
                                    {isMobileMenuOpen && <img src={closeIcon} alt='close icon' />}
                                </span>
                            </button>
                        </li>

                        <div className={styles.rightNav}>
                            {extended && (<>
                                <li>
                                    <button type="button" onClick={handleStartOverClick} aria-label="Start Over">
                                        <span>Start Over</span>
                                    </button>
                                </li>

                                <li>
                                    <button type="button" onClick={handleSaveClick} aria-label="Save">
                                        <span>Save</span>
                                    </button>
                                </li>

                                <li className={styles.altNav}>
                                    <button type="button" onClick={handleShareModalButtonClick} aria-label="Share">
                                        <span>Share Project Link</span>
                                    </button>
                                </li>

                                <li className={styles.altNav}>
                                    <button type="button" onClick={handleDownloadButtonClick} aria-label="Download">
                                        <span>{downloadBtnStatus}</span>
                                    </button>
                                </li>

                                <li className={styles.altNav}>
                                    <PrintLink onClick={closeMenu} />
                                </li>

                                {/* <li>
                                    <ChamUploadButton title="Upload Roomscene" className="" aria-label="Upload Button" onClick={closeMenu} />
                                </li> */}

                                

                                

                                
                                    <li>
                                    <button type="button" onClick={handleFeedbackModalButtonClick} aria-label="Feedback">
                                        <span>Feedback</span>
                                    </button>
                                </li>

                                  <li>
                                    <button onClick={handleHelpButtonClick}>Help</button>
                                </li>
                            </>)}
                        </div>
                    </ul>
                </nav>
            </section>

            <HelpModal 
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
            />

            <ShareModal 
                isOpen={isShareOpen}
                onClose={() => setIsShareOpen(false)}
            />

            <FeedbackModal 
                isOpen={isFeedbackOpen}
                onClose={() => setIsFeedbackOpen(false)}
            />
        </>
    );
}

